import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchPricingCategories } from '../data-provider';
import type { PricingCategories, PricingCategory, PricingCategoryFilters } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal pricing category lookup cache used to resolve pricing category references
 * without repeatedly requesting the same pricing category data.
 */

export const pricingCategoriesQueryOptions = (
  filters: Partial<PricingCategoryFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const pricingCategories = await fetchPricingCategories(filters, { signal });
      put(pricingCategories.list);
      return pricingCategories;
    },
    queryKey: CACHE_KEYS.pricingCategories(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_PRICING_CATEGORIES_CACHE_KEY = btoa('internal-pricing-categories-cache-key');

const INTERNAL_QUERY_PRICING_CATEGORIES_KEY = pricingCategoriesQueryOptions({
  codeDesc: INTERNAL_PRICING_CATEGORIES_CACHE_KEY,
}).queryKey;

const getPricingCategoryFallback = (pricingCategoryId: string): PricingCategory => {
  logger.warn(`Pricing category "${pricingCategoryId}" was not found. Verify the data model.`);
  return {
    codeDesc: pricingCategoryId,
    id: pricingCategoryId,
    label: pricingCategoryId,
    lookupCodeType: 'PRCNCTGR',
  };
};

export const put = async (pricingCategories: PricingCategory[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PRICING_CATEGORIES_KEY);

  const newPricingCategories = pricingCategories.reduce(
    (acc, pricingCategory) => {
      acc[String(pricingCategory.id)] = pricingCategory;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_PRICING_CATEGORIES_KEY, {
    list: Object.values(newPricingCategories),
    map: newPricingCategories,
  });
};

export const getAll = (): PricingCategories | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_PRICING_CATEGORIES_KEY);
};

export const find = async (pricingCategoryId: string): Promise<PricingCategory> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PRICING_CATEGORIES_KEY);

  const pricingCategory = data?.map[pricingCategoryId];
  if (pricingCategory) {
    return pricingCategory;
  }

  try {
    const newData = await queryClient.query(pricingCategoriesQueryOptions({ search: { codeId: pricingCategoryId } }));
    if (newData.map[pricingCategoryId]) {
      void put(newData.list);
      return newData.map[pricingCategoryId];
    }
  } catch {
    return getPricingCategoryFallback(pricingCategoryId);
  }

  return getPricingCategoryFallback(pricingCategoryId);
};

export const findAll = async (pricingCategoryIds: string[]): Promise<PricingCategory[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PRICING_CATEGORIES_KEY);

  const missingIds: string[] = [];
  const pricingCategories: PricingCategory[] = [];

  for (const pricingCategoryId of pricingCategoryIds) {
    const item = data?.map[pricingCategoryId];
    if (item) {
      pricingCategories.push(item);
    } else {
      missingIds.push(pricingCategoryId);
    }
  }

  if (!missingIds.length) {
    return pricingCategories;
  }

  try {
    const newData = await queryClient.query(
      pricingCategoriesQueryOptions({ search: { codeId: missingIds.join(',') } }),
    );
    void put(newData.list);
    for (const missingId of missingIds) {
      const item = newData.map[missingId];
      if (item) {
        pricingCategories.push(item);
      } else {
        pricingCategories.push(getPricingCategoryFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      pricingCategories.push(getPricingCategoryFallback(missingId));
    }
  }

  return pricingCategories;
};
